import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { DEFAULT_MENU } from "@/data/menu";
import { LOGO_URL } from "@/data/homeContent";
import { ArrowLeft, Loader2, Save, Eye, EyeOff, Plus, Trash2, ChevronUp, ChevronDown, RotateCcw } from "lucide-react";

export default function MenuManager() {
  const [menu, setMenu] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/menu").then((res) => setMenu(Array.isArray(res.data) && res.data.length ? res.data : DEFAULT_MENU)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const update = (i, patch) => setMenu((m) => m.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
  const move = (i, dir) => setMenu((m) => {
    const j = i + dir;
    if (j < 0 || j >= m.length) return m;
    const copy = [...m];
    [copy[i], copy[j]] = [copy[j], copy[i]];
    return copy;
  });
  const remove = (i) => setMenu((m) => m.filter((_, idx) => idx !== i));
  const addItem = () => setMenu((m) => [...m, { id: `custom-${Date.now()}`, label: "New Link", type: "link", to: "/", visible: true, children: [] }]);

  const updateChild = (i, ci, patch) => setMenu((m) => m.map((it, idx) => idx === i ? { ...it, children: it.children.map((ch, cidx) => cidx === ci ? { ...ch, ...patch } : ch) } : it));
  const addChild = (i) => setMenu((m) => m.map((it, idx) => idx === i ? { ...it, children: [...(it.children || []), { label: "New Item", to: "/", visible: true }] } : it));
  const removeChild = (i, ci) => setMenu((m) => m.map((it, idx) => idx === i ? { ...it, children: it.children.filter((_, cidx) => cidx !== ci) } : it));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/menu", { menu });
      toast.success("Menu updated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!window.confirm("Reset the menu to the default items?")) return;
    try {
      const { data } = await api.post("/admin/menu/reset");
      setMenu(data);
      toast.success("Menu reset to default");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Reset failed");
    }
  };

  return (
    <div className="min-h-screen bg-[#faf9f6]" data-testid="admin-menu-manager">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/admin" className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-[#800020]" data-testid="menu-back-btn">
            <ArrowLeft className="h-4 w-4" /> Back to Dashboard
          </Link>
          <div className="flex items-center gap-3">
            <button onClick={reset} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#800020]" data-testid="menu-reset-btn"><RotateCcw className="h-3.5 w-3.5" /> Reset</button>
            <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#800020] hover:bg-[#660019] text-white font-medium transition-colors disabled:opacity-60" data-testid="menu-save-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save Menu
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-2">
          <h1 className="font-serif text-3xl text-slate-900">Navigation Menu</h1>
          <button onClick={addItem} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-[#800020] text-[#800020] hover:bg-[#800020] hover:text-white transition-colors text-sm font-medium" data-testid="menu-add-item-btn"><Plus className="h-4 w-4" /> Add Menu Item</button>
        </div>
        <p className="text-slate-500 text-sm mb-6">Show/hide, rename, reorder, add or remove menu items. Changes appear in the website header after saving.</p>

        {loading ? (
          <div className="p-10 text-center text-slate-500"><Loader2 className="h-5 w-5 animate-spin inline mr-2" /> Loading…</div>
        ) : (
          <div className="space-y-4">
            {menu.map((item, i) => (
              <div key={item.id} className={`rounded-xl border bg-white p-5 ${item.visible === false ? "border-slate-200 opacity-70" : "border-slate-200"}`} data-testid={`menu-item-${item.id}`}>
                <div className="flex items-center gap-3 flex-wrap">
                  <button onClick={() => update(i, { visible: item.visible === false })} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium ${item.visible === false ? "bg-slate-100 text-slate-500" : "bg-green-50 text-green-700"}`} data-testid={`menu-toggle-${item.id}`}>
                    {item.visible === false ? <><EyeOff className="h-3.5 w-3.5" /> Hidden</> : <><Eye className="h-3.5 w-3.5" /> Visible</>}
                  </button>
                  <input value={item.label} onChange={(e) => update(i, { label: e.target.value })} className="flex-1 min-w-[160px] border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#800020]" data-testid={`menu-label-${item.id}`} />
                  <span className="text-xs text-slate-400 px-2 py-1 rounded bg-slate-50 border border-slate-200">{item.type}</span>
                  <div className="flex items-center gap-1">
                    <button onClick={() => move(i, -1)} className="p-2 rounded hover:bg-slate-100 text-slate-500" title="Move up"><ChevronUp className="h-4 w-4" /></button>
                    <button onClick={() => move(i, 1)} className="p-2 rounded hover:bg-slate-100 text-slate-500" title="Move down"><ChevronDown className="h-4 w-4" /></button>
                    <button onClick={() => remove(i)} className="p-2 rounded hover:bg-red-50 text-red-500" data-testid={`menu-remove-${item.id}`} title="Remove"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>

                {item.type === "link" && (
                  <div className="mt-3">
                    <label className="block text-xs text-slate-500 mb-1">Links to (path)</label>
                    <input value={item.to || ""} onChange={(e) => update(i, { to: e.target.value })} placeholder="/about" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#800020]" data-testid={`menu-to-${item.id}`} />
                  </div>
                )}

                {item.type === "dropdown" && (
                  <div className="mt-4 pl-4 border-l-2 border-slate-100 space-y-2">
                    <p className="text-xs font-medium text-slate-500">Dropdown items</p>
                    {(item.children || []).map((ch, ci) => (
                      <div key={ci} className="flex items-center gap-2 flex-wrap">
                        <button onClick={() => updateChild(i, ci, { visible: ch.visible === false })} className={`p-1.5 rounded ${ch.visible === false ? "text-slate-400" : "text-green-600"}`} title="Toggle visibility">
                          {ch.visible === false ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                        <input value={ch.label} onChange={(e) => updateChild(i, ci, { label: e.target.value })} className="flex-1 min-w-[140px] border border-slate-300 rounded-lg px-3 py-1.5 text-sm outline-none focus:border-[#800020]" />
                        <input value={ch.to} onChange={(e) => updateChild(i, ci, { to: e.target.value })} className="flex-1 min-w-[140px] border border-slate-300 rounded-lg px-3 py-1.5 text-sm outline-none focus:border-[#800020]" placeholder="/path" />
                        <button onClick={() => removeChild(i, ci)} className="p-1.5 rounded hover:bg-red-50 text-red-500"><Trash2 className="h-4 w-4" /></button>
                      </div>
                    ))}
                    <button onClick={() => addChild(i)} className="inline-flex items-center gap-1.5 text-xs text-[#800020] font-medium mt-1"><Plus className="h-3.5 w-3.5" /> Add dropdown item</button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="mt-6">
          <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-[#800020] hover:bg-[#660019] text-white font-medium transition-colors disabled:opacity-60" data-testid="menu-save-btn-bottom">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save Menu
          </button>
        </div>
      </div>
    </div>
  );
}
